import { Plus } from 'lucide-react'
import type * as React from 'react'
import { cn } from '../../lib/cn'
import type { StageEmptyHint } from './types'

export interface StageEmptyStateProps {
  /** Headline + prompt copy. */
  hint: StageEmptyHint
  /** Accent color (the stage/lane `--stage-accent`) used to tint the icon. */
  accent?: string
  /** Action affordance rendered below the copy — typically the Add button. */
  action?: React.ReactNode
  className?: string
}

/**
 * StageEmptyState — the centered prompt a stage / lane shows when it has
 * no admin blocks. Replaces the lonely bottom "Add" button with a tidy,
 * value-first invitation: a tinted glyph, a headline, a one-line reason,
 * then the action. Sized to content so the host card can shrink to a
 * min-height and center it vertically rather than stretching full-length.
 */
export function StageEmptyState({ hint, accent, action, className }: StageEmptyStateProps) {
  const tint = accent ?? 'var(--pe-text-tertiary)'
  return (
    <div
      className={cn(
        'flex flex-col items-center text-center gap-2 max-w-[220px] mx-auto',
        className,
      )}
      data-testid="pipeline-stage-empty"
    >
      <span
        aria-hidden
        className="flex items-center justify-center w-9 h-9 rounded-full border border-dashed mb-0.5"
        style={{
          borderColor: `color-mix(in srgb, ${tint} 40%, transparent)`,
          color: tint,
          background: `color-mix(in srgb, ${tint} 6%, transparent)`,
        }}
      >
        <Plus size={16} strokeWidth={2} />
      </span>
      <div className="font-mono text-[12px]" style={{ color: 'var(--pe-text-secondary)' }}>
        {hint.title}
      </div>
      <p className="text-[11px] leading-[1.45]" style={{ color: 'var(--pe-text-tertiary)' }}>
        {hint.description}
      </p>
      {action && <div className="w-full mt-1.5">{action}</div>}
    </div>
  )
}
